import { FC } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { FileUploadFilter } from "../../../../components/filters/Whitespace/FileUploadFilter";
import { useMatchDataProcessingStore } from "../../../../store/MatchDataProcessingStore";
import { useWorkspaceStore } from "../../../../store/workspace/WorkspaceStore";
import { CompanyMatchUploadResponse } from "../../../../types/companyMatch";
import { Workspace } from "../../../../types/workspace";
import { Divider } from "@mui/material";
import icp_example from "../../../../assets/img/company_match_column_spec_v3.png";
import { lightScrollbarStyle } from "../../../../styles/lightScrollbarStyle";
import { UITooltip } from "../../../../components/UITooltip";

export const WhitespaceConfiguration: FC = () => {
  const workspaceId = useWorkspaceStore((state) => state.draft?.id);
  const setWorkspace = useWorkspaceStore((state) => state.setDraft);
  const workspaceWhitespace = useWorkspaceStore(
    (state) => state.draft?.configuration?.whitespace
  );

  // Get processing functions from the MatchDataProcessingStore
  const startProcessing = useMatchDataProcessingStore(
    (state) => state.startProcessing
  );
  const stopProcessing = useMatchDataProcessingStore(
    (state) => state.stopProcessing
  );
  const processingFile = useMatchDataProcessingStore((state) =>
    workspaceId ? state.getProcessingFile(workspaceId, "whitespace") : null
  );

  const clearWhitespace = async () => {
    if (!workspaceId) return;

    stopProcessing(workspaceId, "whitespace");

    setWorkspace((prev) => ({
      ...(prev as Workspace),
      configuration: {
        ...(prev as Workspace).configuration,
        whitespace: null,
      },
    }));
  };

  const handleUpdateMatchData = async (
    matchData: CompanyMatchUploadResponse
  ) => {
    if (!workspaceId || !matchData.fileId) return;

    startProcessing(workspaceId, matchData, "whitespace");
  };

  const handleHierarchyMatchingChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const isChecked = event.target.checked;

    // Update workspace configuration
    setWorkspace((prev) => {
      const prevWorkspace = prev as Workspace;
      const prevWhitespace = prevWorkspace.configuration?.whitespace;
      if (!prevWhitespace) {
        // If whitespace does not exist, do not update (or handle as needed)
        return prevWorkspace;
      }
      return {
        ...prevWorkspace,
        configuration: {
          ...prevWorkspace.configuration,
          whitespace: {
            ...prevWhitespace,
            enableHierarchyClassification: isChecked,
          },
        },
      };
    });
  };

  // Use processing data if available, otherwise use workspaceWhitespace
  const matchData =
    processingFile || workspaceWhitespace
      ? {
          fileId: processingFile?.fileId || workspaceWhitespace?.fileId || "",
          fileName:
            processingFile?.fileName || workspaceWhitespace?.fileName || "",
          uploaded:
            processingFile?.uploaded || workspaceWhitespace?.uploaded || "",
          lastMatched:
            processingFile?.lastMatched ||
            workspaceWhitespace?.lastMatched ||
            null,
          totalRows:
            processingFile?.totalRows || workspaceWhitespace?.totalRows || 0,
          processedRows:
            processingFile?.processedRows ||
            workspaceWhitespace?.processedRows ||
            0,
          matchedRows:
            processingFile?.matchedRows ||
            workspaceWhitespace?.matchedRows ||
            0,
          invalidRows:
            processingFile?.invalidRows ||
            workspaceWhitespace?.invalidRows ||
            0,
          processedChunks:
            processingFile?.processedChunks ||
            workspaceWhitespace?.processedChunks ||
            0,
          totalChunks:
            processingFile?.totalChunks ||
            workspaceWhitespace?.totalChunks ||
            0,
          filterMethod: "EXCLUDE_MATCHED",
        }
      : null;

  return (
    <Box
      height="100%"
      width={680}
      sx={{ overflowY: "scroll", ...lightScrollbarStyle }}
    >
      <Stack direction="row" justifyContent="space-between" marginBottom={2}>
        <Typography variant="subtitle1" fontWeight="medium">
          Define Whitespace
        </Typography>
        {workspaceWhitespace?.fileId && (
          <Button
            variant="outlined"
            color="primary"
            size="small"
            onClick={clearWhitespace}
          >
            Clear
          </Button>
        )}
      </Stack>
      <Divider />
      <Typography padding={1} color="secondary">
        Upload a CSV file of your current customers to identify whitespace
        opportunities in your market. Our matching algorithm will identify these
        companies in our database and classify all remaining companies as
        whitespace opportunities. To maximize matching accuracy, please format
        your column headers exactly as shown below (order doesn't matter, not
        case sensitive, each field is optional)
      </Typography>
      <Stack alignItems="center" justifyContent="center">
        <img alt={"column-headers"} width="490px" src={icp_example} />
      </Stack>
      <Typography padding={1} color="secondary">
        The quality of your match depends on the data you provide. Including
        additional fields like DUNS numbers, company URLs, and location details
        significantly improves match accuracy and gives you more reliable
        whitespace analysis.
      </Typography>

      <FileUploadFilter
        matchData={matchData}
        onUpdateMatchData={handleUpdateMatchData}
        showAlert={false}
      />

      <Box mt={3} mb={4}>
        <Box display="flex" alignItems="center" gap={1} mb={1}>
          <Typography variant="subtitle1" fontWeight="medium">
            Hierarchy Matching
          </Typography>
          <UITooltip
            title="When enabled, this feature analyzes corporate hierarchies to categorize whitespace opportunities into two types: 'Expansion Whitespace' (companies related to your existing customers through corporate parent-child relationships) and 'Prospect Whitespace' (companies with no connection to your current customer base). Without this feature enabled, all non-customer companies are simply labeled as 'Whitespace'"
            placement="top"
            sx={{
              fontSize: "14px",
              lineHeight: 1.4,
            }}
          >
            <HelpOutlineIcon
              sx={{
                fontSize: 18,
                color: "text.secondary",
                cursor: "help",
                "&:hover": {
                  color: "primary.main",
                },
              }}
            />
          </UITooltip>
        </Box>
        <FormControlLabel
          control={
            <Checkbox
              checked={
                workspaceWhitespace?.enableHierarchyClassification ?? false
              }
              onChange={handleHierarchyMatchingChange}
              disabled={!workspaceWhitespace?.fileId}
            />
          }
          label="Distinguish whitespace between expansion whitespace (related to existing customers) and prospect whitespace (completely unrelated entities)"
        />
      </Box>
    </Box>
  );
};
